/**
 * OfflineBanner - Shows when user is offline with sync status
 */
import { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, Check, Cloud } from 'lucide-react';
import { cn } from '@/lib/utils';

export const OfflineBanner = ({ 
  isOnline, 
  isSyncing, 
  pendingCount,
  onSync 
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  // Track when coming back online
  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline && isOnline && pendingCount === 0) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, pendingCount]);

  // Don't show anything if online and no pending/success
  if (isOnline && !isSyncing && pendingCount === 0 && !showSuccess) {
    return null;
  }

  return (
    <div 
      className={cn(
        "fixed top-0 left-0 right-0 z-[10001] transition-all duration-300",
        "flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium",
        !isOnline && "bg-amber-500 text-white",
        isOnline && isSyncing && "bg-blue-500 text-white",
        isOnline && pendingCount > 0 && !isSyncing && "bg-amber-500 text-white",
        showSuccess && "bg-green-500 text-white"
      )}
      style={{}}
      data-testid="offline-banner"
    >
      {!isOnline ? (
        <>
          <WifiOff className="w-4 h-4" />
          <span>You're offline — changes will sync when connected</span>
          {pendingCount > 0 && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
              {pendingCount} pending
            </span>
          )}
        </>
      ) : isSyncing ? (
        <>
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Syncing changes...</span>
        </>
      ) : showSuccess ? (
        <>
          <Check className="w-4 h-4" />
          <span>All changes synced!</span>
        </>
      ) : pendingCount > 0 ? (
        <>
          <Cloud className="w-4 h-4" />
          <span>{pendingCount} change{pendingCount > 1 ? 's' : ''} pending</span>
          <button 
            onClick={onSync}
            className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded-full text-xs transition-colors"
          >
            Sync now
          </button>
        </>
      ) : null}
    </div>
  );
};

export default OfflineBanner;
